import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import "./ModalPersonasAgenda.css";
import {
  getDuplicados,
  getPersonasAgenda,
  descartarDuplicado,
  juntarDuplicado,
  archivarPersona,
  eliminarPersona,
  juntarPersonas,
  editarPersona,
} from "../../../../../services/citas";

// Cómo llegó cada persona a la agenda
const ORIGENES = {
  interno: "Consultorio",
  whatsapp: "WhatsApp",
  telefono: "Teléfono",
  publico: "Página pública",
  asistente: "Asistente",
  gmail: "Gmail",
};


const SEXOS = { M: "Masculino", F: "Femenino", O: "Otro" };

// Lo que el médico puede corregir de una persona sin alta
const CAMPOS_EDITABLES = [
  "nombre", "apellido_paterno", "apellido_materno",
  "telefono", "correo", "fecha_nacimiento", "sexo", "curp",
];

// El servidor puede responder { error: "..." } o { telefono: ["..."] }: sacamos el primer mensaje
const mensajeError = (data) => {
  if (!data) return "No se pudo completar la acción.";
  if (typeof data.error === "string") return data.error;
  const primero = Object.values(data)[0];
  if (Array.isArray(primero)) return primero[0];
  if (typeof primero === "string") return primero;
  return "No se pudo completar la acción.";
};

// Un campo del formulario (etiqueta arriba, campo abajo)
const Campo = ({ etiqueta, children }) => (
  <label className="pa-campo">
    <span>{etiqueta}</span>
    {children}
  </label>
);

// "AAAA-MM-DD" -> "DD/MM/AAAA" (sin brincar de día por la zona horaria)
const formatearFecha = (valor) => {
  if (!valor) return "—";
  const [a, m, d] = String(valor).slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
};

// Fecha y hora del servidor -> "AAAA-MM-DD" en hora de CDMX (igual que Gestión de Pacientes)
const toDateKeyCDMX = (iso) => {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
};

// A quién se propone conservar: la del expediente; si no, la que tiene más citas
const baseSugerida = (par) => {
  if (par.base_obligatoria) return par.base_obligatoria;
  const { persona_a: a, persona_b: b } = par;
  return (b.numero_citas || 0) > (a.numero_citas || 0) ? b.id : a.id;
};

// Tarjeta de una persona dentro de un par de duplicados
const TarjetaPersona = ({ persona, grupo, elegida, bloqueada, onElegir }) => (
  <label
    className={`pa-persona ${elegida ? "pa-persona--base" : ""} ${
      bloqueada && !elegida ? "pa-persona--bloqueada" : ""
    }`}
  >
    <div className="pa-persona__top">
      <input
        type="radio"
        name={grupo}
        checked={elegida}
        disabled={bloqueada && !elegida}
        onChange={onElegir}
      />
      <span>{elegida ? "Se conserva" : "Se junta"}</span>
    </div>

    <strong>{persona.nombre_completo || "(Sin nombre)"}</strong>

    <div className="pa-etiquetas">
      {persona.con_expediente && <span className="pa-etiqueta pa-etiqueta--alta">Con expediente</span>}
      {persona.archivada && <span className="pa-etiqueta">Archivada</span>}
    </div>

    <small>Teléfono: {persona.telefono || "—"}</small>
    <small>Correo: {persona.correo || "—"}</small>
    <small>Nacimiento: {formatearFecha(persona.fecha_nacimiento)}</small>
    <small>Origen: {ORIGENES[persona.origen] || persona.origen || "—"}</small>
    <small>
      Citas: {persona.numero_citas || 0} · Última: {formatearFecha(persona.ultima_cita)}
    </small>
  </label>
);

const ModalPersonasAgenda = ({ isOpen, onClose, onUpdated, pestanaInicial = "duplicados" }) => {
  const [pestana, setPestana] = useState(pestanaInicial);
  const [duplicados, setDuplicados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ocupado, setOcupado] = useState(false); // una acción a la vez
  const [huboCambios, setHuboCambios] = useState(false);

  // Duplicados: a quién conserva el médico en cada par { parId: personaId }
  const [bases, setBases] = useState({});

  // Personas sin alta: filtros, selección y confirmación de eliminar
  const [search, setSearch] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [verArchivadas, setVerArchivadas] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [baseSeleccion, setBaseSeleccion] = useState("");
  const [porEliminar, setPorEliminar] = useState(null);
  // "Ver datos" / "Editar" de una persona sin alta
  const [abierta, setAbierta] = useState(null);   // id de la persona con sus datos desplegados
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});

  const cargar = async (mostrarCargando = true) => {
    if (mostrarCargando) setLoading(true);
    try {
      const [dups, pers] = await Promise.all([getDuplicados(), getPersonasAgenda()]);
      const listaDups = dups || [];
      const listaPers = pers || [];

      setDuplicados(listaDups);
      setPersonas(listaPers);

      // Conserva lo que el médico ya había elegido; si hay expediente, ese manda
      setBases((prev) => {
        const nuevas = {};
        listaDups.forEach((par) => {
          nuevas[par.id] = par.base_obligatoria || prev[par.id] || baseSugerida(par);
        });
        return nuevas;
      });

      // Quita de la selección a quien ya no exista (por ejemplo, porque se juntó)
      setSeleccion((prev) => prev.filter((id) => listaPers.some((p) => p.id === id)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setPestana(pestanaInicial);
    setHuboCambios(false);
    setSeleccion([]);
    setBaseSeleccion("");
    setPorEliminar(null);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Al cerrar: si algo cambió, la agenda se vuelve a cargar
  const cerrar = () => {
    if (huboCambios) onUpdated?.();
    onClose();
  };

  // Ejecuta una acción, muestra el mensaje y recarga las listas
  const ejecutar = async (accion, mensajeOk) => {
    if (ocupado) return false;
    setOcupado(true);
    const res = await accion();
    setOcupado(false);

    if (!res.ok) {
      toast.error(mensajeError(res.data));
      return false;
    }
    if (mensajeOk) toast.success(mensajeOk);
    setHuboCambios(true);
    await cargar(false);
    return true;
  };

  // ===== Duplicados =====

  const handleDescartar = (par) =>
    ejecutar(
      () => descartarDuplicado(par.id),
      "Listo. Estas personas ya no aparecerán como posible duplicado."
    );

  const handleJuntarPar = (par) =>
    ejecutar(
      () => juntarDuplicado(par.id, bases[par.id]),
      "Se juntaron en una sola persona. Sus citas quedaron en la que se conserva."
    );

  // ===== Personas sin alta =====

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return personas.filter((p) => {
      if (!verArchivadas && p.archivada) return false;

      if (q) {
        const texto = `${p.nombre_completo || ""} ${p.telefono || ""} ${p.correo || ""}`.toLowerCase();
        if (!texto.includes(q)) return false;
      }

      if (fechaDesde || fechaHasta) {
        const fechaRegistro = toDateKeyCDMX(p.creado_en);
        if (fechaDesde && fechaRegistro < fechaDesde) return false;
        if (fechaHasta && fechaRegistro > fechaHasta) return false; // incluyente
      }
      return true;
    });
  }, [personas, search, fechaDesde, fechaHasta, verArchivadas]);

  const toggleSeleccion = (id) => {
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const nombreDe = (id) => personas.find((p) => p.id === id)?.nombre_completo || "(Sin nombre)";

  // La base debe ser una de las seleccionadas; si no eligió, la primera
  const baseEfectiva = seleccion.includes(baseSeleccion) ? baseSeleccion : seleccion[0] || "";

  const handleJuntarSeleccion = async () => {
    if (seleccion.length < 2) return;
    const otras = seleccion.filter((id) => id !== baseEfectiva);
    const ok = await ejecutar(
      () => juntarPersonas({ baseId: baseEfectiva, otrasIds: otras }),
      `Se juntaron ${seleccion.length} personas en una sola.`
    );
    if (ok) {
      setSeleccion([]);
      setBaseSeleccion("");
    }
  };

  // El switch encendido = activa; apagado = archivada (igual que Gestión de Pacientes)
  const handleArchivar = (persona, activa) =>
    ejecutar(
      () => archivarPersona(persona.id, !activa),
      activa ? "La persona volvió a la lista." : "Persona archivada. Sus citas no cambian."
    );

  const handleEliminar = async (persona) => {
    const ok = await ejecutar(
      () => eliminarPersona(persona.id),
      "Persona eliminada junto con sus citas."
    );
    if (ok) setPorEliminar(null);
  };

  
  // ===== Ver / editar datos =====

  const toggleDatos = (id) => {
    setAbierta((prev) => (prev === id ? null : id));
    setEditando(false);
    setPorEliminar(null);
  };

  const iniciarEdicion = (persona) => {
    const inicial = {};
    CAMPOS_EDITABLES.forEach((campo) => {
      inicial[campo] = persona[campo] || "";
    });
    setForm(inicial);
    setEditando(true);
  };

  const cambiarCampo = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const guardarEdicion = async (persona) => {
    const datos = {
      ...form,
      fecha_nacimiento: form.fecha_nacimiento || null, // fecha vacía = sin fecha
      sexo: form.sexo || null,
    };
    const ok = await ejecutar(() => editarPersona(persona.id, datos), "Datos actualizados.");
    if (ok) setEditando(false);
  };

  if (!isOpen) return null;

  return (
    <div className="pa-backdrop">
      <div className="pa-modal">
        <div className="pa-header">
          <h3>Personas de mi agenda</h3>
          <button type="button" className="pa-cerrar-x" onClick={cerrar} aria-label="Cerrar">
            ×
          </button>
        </div>

        {/* Pestañas */}
        <div className="pa-tabs">
          <button
            type="button"
            className={`pa-tab ${pestana === "duplicados" ? "pa-tab--activa" : ""}`}
            onClick={() => setPestana("duplicados")}
          >
            Posibles duplicados
            {duplicados.length > 0 && <span className="pa-contador">{duplicados.length}</span>}
          </button>
          <button
            type="button"
            className={`pa-tab ${pestana === "sin-alta" ? "pa-tab--activa" : ""}`}
            onClick={() => setPestana("sin-alta")}
          >
            Personas sin alta
          </button>
        </div>

        <div className="pa-contenido">
          {loading ? (
            <p className="pa-vacio">Cargando...</p>
          ) : pestana === "duplicados" ? (
            /* ===== Pestaña: Posibles duplicados ===== */
            <>
              <p className="pa-ayuda">
                Estas personas se parecen por nombre o comparten teléfono o correo. Elija cuál se
                conserva: las citas de la otra pasarán a ella.
              </p>

              {duplicados.length === 0 ? (
                <p className="pa-vacio">No hay posibles duplicados por revisar. ✅</p>
              ) : (
                duplicados.map((par) => {
                  const base = bases[par.id];
                  const bloqueada = !!par.base_obligatoria;
                  return (
                    <div key={par.id} className="pa-par">
                      <div className="pa-par__motivo">{par.motivo_texto}</div>

                      <div className="pa-par__personas">
                        {[par.persona_a, par.persona_b].map((persona) => (
                          <TarjetaPersona
                            key={persona.id}
                            persona={persona}
                            grupo={`base-${par.id}`}
                            elegida={base === persona.id}
                            bloqueada={bloqueada}
                            onElegir={() => setBases((prev) => ({ ...prev, [par.id]: persona.id }))}
                          />
                        ))}
                      </div>

                      {bloqueada && (
                        <p className="pa-nota">La persona con expediente siempre se conserva.</p>
                      )}

                      <div className="pa-par__acciones">
                        <button
                          type="button"
                          className="pa-btn pa-btn--secundario"
                          disabled={ocupado}
                          onClick={() => handleDescartar(par)}
                        >
                          No son la misma persona
                        </button>
                        <button
                          type="button"
                          className="pa-btn pa-btn--primario"
                          disabled={ocupado || !base}
                          onClick={() => handleJuntarPar(par)}
                        >
                          Juntar en una
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          ) : (
            /* ===== Pestaña: Personas sin alta ===== */
            <>
              <p className="pa-ayuda">
                Personas agendadas que aún no tienen expediente. Apague el switch para archivar
                (sus citas no cambian). Marque dos o más para juntarlas en una sola.
              </p>

              <input
                type="text"
                className="pa-search"
                placeholder="Buscar por nombre, teléfono o correo"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <div className="pa-filtros">
                <div className="pa-filtro-campo">
                  <label>Registradas desde</label>
                  <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
                </div>
                <div className="pa-filtro-campo">
                  <label>Hasta</label>
                  <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
                </div>
                <label className="pa-check-archivadas">
                  <input
                    type="checkbox"
                    checked={verArchivadas}
                    onChange={(e) => setVerArchivadas(e.target.checked)}
                  />
                  Mostrar archivadas
                </label>
              </div>

              <div className="pa-lista">
                {filtradas.length === 0 ? (
                  <p className="pa-vacio">No hay personas que coincidan.</p>
                ) : (
                  filtradas.map((p) => (
                    <div key={p.id}>
                      <div className={`pa-row ${p.archivada ? "pa-row--archivada" : ""}`}>
                        <input
                          type="checkbox"
                          className="pa-row__check"
                          checked={seleccion.includes(p.id)}
                          onChange={() => toggleSeleccion(p.id)}
                          aria-label={`Seleccionar a ${p.nombre_completo}`}
                        />

                        <div className="pa-row__info">
                          <strong>{p.nombre_completo || "(Sin nombre)"}</strong>
                          <small>
                            {p.telefono || p.correo || "Sin contacto"} ·{" "}
                            {ORIGENES[p.origen] || p.origen} · {p.numero_citas || 0} cita(s) · Última:{" "}
                            {formatearFecha(p.ultima_cita)}
                          </small>
                          <small>Registro: {formatearFecha(toDateKeyCDMX(p.creado_en))}</small>
                        </div>
                        <button
                          type="button"
                          className={`pa-btn-datos ${abierta === p.id ? "pa-btn-datos--activo" : ""}`}
                          onClick={() => toggleDatos(p.id)}
                        >
                          {abierta === p.id ? "Ocultar" : "Ver datos"}
                        </button>


                        <label
                          className="pa-switch"
                          title={p.archivada ? "Archivada: enciéndalo para que vuelva a la lista" : "Activa: apáguelo para archivarla"}
                        >
                          <input
                            type="checkbox"
                            checked={!p.archivada}
                            disabled={ocupado}
                            onChange={(e) => handleArchivar(p, e.target.checked)}
                          />
                          <span className="pa-slider" />
                        </label>

                        <button
                          type="button"
                          className="pa-btn-eliminar"
                          title="Eliminar"
                          aria-label={`Eliminar a ${p.nombre_completo}`}
                          disabled={ocupado}
                          onClick={() => {
                            setAbierta(null);
                            setPorEliminar(porEliminar === p.id ? null : p.id);
                          }}
                        >
                          🗑️
                        </button>
                      </div>

                      {porEliminar === p.id && (
                        <div className="pa-confirmar">
                          <span>
                            ¿Eliminar a <strong>{p.nombre_completo}</strong> y sus {p.numero_citas || 0}{" "}
                            cita(s)? Esta acción no se puede deshacer.
                          </span>
                          <div className="pa-confirmar__botones">
                            <button
                              type="button"
                              className="pa-btn pa-btn--secundario"
                              onClick={() => setPorEliminar(null)}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="pa-btn pa-btn--peligro"
                              disabled={ocupado}
                              onClick={() => handleEliminar(p)}
                            >
                              Sí, eliminar
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {abierta === p.id && (
                        <div className="pa-datos">
                          {!editando ? (
                            <>
                              <div className="pa-datos__lista">
                                <div><span>Nombre</span><strong>{p.nombre || "—"}</strong></div>
                                <div><span>Apellido paterno</span><strong>{p.apellido_paterno || "—"}</strong></div>
                                <div><span>Apellido materno</span><strong>{p.apellido_materno || "—"}</strong></div>
                                <div><span>Teléfono</span><strong>{p.telefono || "—"}</strong></div>
                                <div><span>Correo</span><strong>{p.correo || "—"}</strong></div>
                                <div><span>Fecha de nacimiento</span><strong>{formatearFecha(p.fecha_nacimiento)}</strong></div>
                                <div><span>Sexo</span><strong>{SEXOS[p.sexo] || "—"}</strong></div>
                                <div><span>CURP</span><strong>{p.curp || "—"}</strong></div>
                              </div>
                              <div className="pa-datos__botones">
                                <button
                                  type="button"
                                  className="pa-btn pa-btn--primario"
                                  onClick={() => iniciarEdicion(p)}
                                >
                                  Editar
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="pa-datos__form">
                                <Campo etiqueta="Nombre *">
                                  <input
                                    type="text"
                                    maxLength={50}
                                    value={form.nombre}
                                    onChange={(e) => cambiarCampo("nombre", e.target.value)}
                                  />
                                </Campo>
                                <Campo etiqueta="Apellido paterno">
                                  <input
                                    type="text"
                                    maxLength={50}
                                    value={form.apellido_paterno}
                                    onChange={(e) => cambiarCampo("apellido_paterno", e.target.value)}
                                  />
                                </Campo>
                                <Campo etiqueta="Apellido materno">
                                  <input
                                    type="text"
                                    maxLength={50}
                                    value={form.apellido_materno}
                                    onChange={(e) => cambiarCampo("apellido_materno", e.target.value)}
                                  />
                                </Campo>
                                <Campo etiqueta="Teléfono">
                                  <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={20}
                                    value={form.telefono}
                                    onChange={(e) => cambiarCampo("telefono", e.target.value)}
                                  />
                                </Campo>
                                <Campo etiqueta="Correo">
                                  <input
                                    type="email"
                                    maxLength={100}
                                    value={form.correo}
                                    onChange={(e) => cambiarCampo("correo", e.target.value)}
                                  />
                                </Campo>
                                <Campo etiqueta="Fecha de nacimiento">
                                  <input
                                    type="date"
                                    value={form.fecha_nacimiento}
                                    onChange={(e) => cambiarCampo("fecha_nacimiento", e.target.value)}
                                  />
                                </Campo>
                                <Campo etiqueta="Sexo">
                                  <select
                                    value={form.sexo}
                                    onChange={(e) => cambiarCampo("sexo", e.target.value)}
                                  >
                                    <option value="">Sin especificar</option>
                                    <option value="M">Masculino</option>
                                    <option value="F">Femenino</option>
                                    <option value="O">Otro</option>
                                  </select>
                                </Campo>
                                <Campo etiqueta="CURP">
                                  <input
                                    type="text"
                                    maxLength={18}
                                    value={form.curp}
                                    onChange={(e) => cambiarCampo("curp", e.target.value.toUpperCase())}
                                  />
                                </Campo>
                              </div>
                              <p className="pa-nota">* Obligatorio. Capture al menos un teléfono o un correo.</p>
                              <div className="pa-datos__botones">
                                <button
                                  type="button"
                                  className="pa-btn pa-btn--secundario"
                                  disabled={ocupado}
                                  onClick={() => setEditando(false)}
                                >
                                  Cancelar
                                </button>
                                <button
                                  type="button"
                                  className="pa-btn pa-btn--primario"
                                  disabled={ocupado}
                                  onClick={() => guardarEdicion(p)}
                                >
                                  Guardar cambios
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Barra para juntar: aparece al marcar personas */}
              {seleccion.length === 1 && (
                <p className="pa-nota">Seleccione al menos 2 personas para juntarlas.</p>
              )}
              {seleccion.length >= 2 && (
                <div className="pa-juntar-barra">
                  <span>
                    {seleccion.length} seleccionadas. Se conserva:
                  </span>
                  <select value={baseEfectiva} onChange={(e) => setBaseSeleccion(e.target.value)}>
                    {seleccion.map((id) => (
                      <option key={id} value={id}>
                        {nombreDe(id)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="pa-btn pa-btn--primario"
                    disabled={ocupado}
                    onClick={handleJuntarSeleccion}
                  >
                    Juntar seleccionadas
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="pa-footer">
          <button type="button" className="pa-btn pa-btn--secundario" onClick={cerrar}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalPersonasAgenda;